import React from "react";
import { BrowserRouter, Route, Navigate, Routes } from "react-router-dom";
import ScrollToTop from "components/ScrollToTop";
import ErrorBoundary from "components/ErrorBoundary";
import NotFound from "pages/NotFound";
import MainDashboard from './pages/main-dashboard';
import ROICalculatorAnalysisEngine from './pages/roi-calculator-analysis-engine';
import EcommerceROICalculator from './pages/ecommerce-roi-calculator';
import BusinessSettingsConfiguration from './pages/business-settings-configuration';
import Auth from './pages/Auth';
import Onboarding from './pages/Onboarding';
import VATTaxCalculator from './pages/vat-tax-calculator';
import PricingMarginCalculator from './pages/pricing-margin-calculator';
import CashFlowForecastCalculator from "pages/cash-flow-forecast-calculator";
import PromoDiscountImpactCalculator from './pages/promo-discount-impact-calculator';
import COGSPercentageCalculator from './pages/cogs-percentage-calculator';
import BundlePricingCalculator from './pages/bundle-pricing-calculator';
import BusinessBudgetManagerAnalyticsDashboard from './pages/business-budget-manager-analytics-dashboard';
import ProjectManagementDashboard from 'pages/project-management-dashboard';
import LeadROICalculator from './pages/lead-roi-calculator-analysis-engine';
import PlatformConfigurationCostManagement from './pages/platform-configuration-cost-management/index';
import AdminLogin from './pages/admin-login';
import AdminDashboard from './pages/admin-dashboard';
import NotificationBar from './components/NotificationBar';
import SupportTickets from './pages/support-tickets';
import ResetPassword from './pages/ResetPassword';
import AssetManagementOverview from "pages/asset-management-overview";
import AssetManagementDetails from "pages/asset-management-details";
import AssetManagementAnalytics from "pages/asset-management-analytics";
import { useAuth } from './contexts/AuthContext';
import SalesPerformanceTracker from './pages/sales-performance-tracker';
import SalesPerformanceMonthlyData from './pages/sales-performance-monthly-data';

// Helper function to check if URL has recovery type
const isPasswordRecoveryFlow = () => {
  // Check query parameters for type=recovery (Supabase sends this)
  const params = new URLSearchParams(window.location?.search);
  return params?.get('type') === 'recovery';
};

// Protected Route wrapper component
function ProtectedRoute({ children }) {
  const { user, loading, isProfileComplete } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-teal-900 via-blue-900 to-cyan-900">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-white mb-4 mx-auto"></div>
          <p className="text-white text-lg">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/auth" replace />;
  }

  if (!isProfileComplete) {
    return <Navigate to="/onboarding" replace />;
  }

  return children;
}

// Auth Route wrapper - redirects authenticated users to dashboard
// UNLESS it's a password recovery flow
function AuthRoute({ children }) {
  const { user, loading, isProfileComplete } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-teal-900 via-blue-900 to-cyan-900">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-white mb-4 mx-auto"></div>
          <p className="text-white text-lg">Loading...</p>
        </div>
      </div>
    );
  }

  // CRITICAL: Allow password recovery flow even when authenticated
  if (isPasswordRecoveryFlow()) {
    return children;
  }

  if (user && isProfileComplete) {
    return <Navigate to="/" replace />;
  }

  return children;
}

// Onboarding Route wrapper - only allows incomplete profiles
function OnboardingRoute({ children }) {
  const { user, loading, isProfileComplete } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-teal-900 via-blue-900 to-cyan-900">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-white mb-4 mx-auto"></div>
          <p className="text-white text-lg">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/auth" replace />;
  }

  if (isProfileComplete) {
    return <Navigate to="/" replace />;
  }

  return children;
}

export default function AppRoutes() {
  return (
    <BrowserRouter>
      <ErrorBoundary>
        <ScrollToTop />
        <NotificationBar />
        <Routes>
          {/* Public Auth Routes */}
          <Route path="/auth" element={<AuthRoute><Auth /></AuthRoute>} />
          <Route path="/login" element={<AuthRoute><Auth /></AuthRoute>} />
          
          {/* CRITICAL: Password reset must bypass auth redirect for recovery flow */}
          <Route path="/reset-password" element={<AuthRoute><ResetPassword /></AuthRoute>} />
          
          <Route path="/admin-login" element={<AdminLogin />} />
          
          {/* Onboarding Route */}
          <Route path="/onboarding" element={<OnboardingRoute><Onboarding /></OnboardingRoute>} />
          
          {/* Protected Routes */}
          <Route path="/" element={<ProtectedRoute><MainDashboard /></ProtectedRoute>} />
          <Route path="/roi-calculator-analysis-engine" element={<ProtectedRoute><ROICalculatorAnalysisEngine /></ProtectedRoute>} />
          <Route path="/ecommerce-roi-calculator" element={<ProtectedRoute><EcommerceROICalculator /></ProtectedRoute>} />
          <Route path="/lead-roi-calculator-analysis-engine" element={<ProtectedRoute><LeadROICalculator /></ProtectedRoute>} />
          <Route path="/platform-configuration-cost-management" element={<ProtectedRoute><PlatformConfigurationCostManagement /></ProtectedRoute>} />
          <Route path="/business-settings-configuration" element={<ProtectedRoute><BusinessSettingsConfiguration /></ProtectedRoute>} />
          <Route path="/project-management-dashboard" element={<ProtectedRoute><ProjectManagementDashboard /></ProtectedRoute>} />
          <Route path="/vat-tax-calculator" element={<ProtectedRoute><VATTaxCalculator /></ProtectedRoute>} />
          <Route path="/pricing-margin-calculator" element={<ProtectedRoute><PricingMarginCalculator /></ProtectedRoute>} />
          <Route path="/cash-flow-forecast-calculator" element={<ProtectedRoute><CashFlowForecastCalculator /></ProtectedRoute>} />
          <Route path="/promo-discount-impact-calculator" element={<ProtectedRoute><PromoDiscountImpactCalculator /></ProtectedRoute>} />
          <Route path="/asset-management-overview" element={<ProtectedRoute><AssetManagementOverview /></ProtectedRoute>} />
          <Route path="/asset-management-analytics" element={<ProtectedRoute><AssetManagementAnalytics /></ProtectedRoute>} />
          <Route path="/asset-details/:id" element={<ProtectedRoute><AssetManagementDetails /></ProtectedRoute>} />
          <Route path="/cogs-percentage-calculator" element={<ProtectedRoute><COGSPercentageCalculator /></ProtectedRoute>} />
          <Route path="/bundle-pricing-calculator" element={<ProtectedRoute><BundlePricingCalculator /></ProtectedRoute>} />
          <Route path="/business-budget-manager-analytics-dashboard" element={<ProtectedRoute><BusinessBudgetManagerAnalyticsDashboard /></ProtectedRoute>} />
          <Route path="/support-tickets" element={<ProtectedRoute><SupportTickets /></ProtectedRoute>} />
          <Route path="/admin-dashboard" element={<ProtectedRoute><AdminDashboard /></ProtectedRoute>} />
          
          <Route path="/sales-performance-tracker" element={<ProtectedRoute><SalesPerformanceTracker /></ProtectedRoute>} />
          <Route path="/sales-performance-monthly-data" element={<ProtectedRoute><SalesPerformanceMonthlyData /></ProtectedRoute>} />
          
          {/* NotFound catch-all route MUST be last */}
          <Route path="*" element={<NotFound />} />
        </Routes>
      </ErrorBoundary>
    </BrowserRouter>
  );
}